import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import * as _ from 'lodash';

import { RetailerPrivilege } from 'helpers/team-user-roles';
import { AccessState, Brand } from 'shared/models';
import { FcProductName } from 'types';

import { NavigationLink } from '../../../retailer-platform/brand-shop/brand-shop-navigation/types';
import { getBrandFcProductsConnectionStatus } from '../../../retailer-platform/brands/helpers';
import { BrandShopService } from '../../../retailer-platform/brand-shop/brand-shop.service';
import { BrandsService } from '../../../retailer-platform/brands/providers/brands.service';
import { FcProductSuites } from '../../../retailer-platform/brands/types';
import {
    brandShopNavigationDefaultLinksByFcProduct,
    getNavigationLinksByFcProducts,
    parseRouteTargetToString
} from '../../../retailer-platform/brand-shop/brand-shop-navigation/utils';
import {
    brandShopFcProductPageRouteMapper,
    getSelectedProductSuiteFromRoute
} from '../../../retailer-platform/brand-shop/utils';
import { UserService } from '../user.service';
import { LandingPageModel } from 'app/b2b-order/models/landing-page.model';

@Injectable({
    providedIn: 'root'
})
export class RetailerBrandShopGuard {
    constructor(
        private brandShopService: BrandShopService,
        private brandsService: BrandsService,
        private userService: UserService,
        private router: Router,
        private landingPageModel: LandingPageModel
    ) {}

    canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): UrlTree | Observable<boolean | UrlTree> {
        const brandId = route.params.brandId;

        const isALessPrivilegedUser = this.userService.hasAtLeastOnePrivilege([
            RetailerPrivilege.MarketingManager,
            RetailerPrivilege.Viewer
        ]);

        const userSelectedProductSuite = getSelectedProductSuiteFromRoute(state.url);

        if (!userSelectedProductSuite && isALessPrivilegedUser) {
            const fallbackUrl = brandShopFcProductPageRouteMapper(brandId)[FcProductSuites.Content];
            return this.router.parseUrl(fallbackUrl);
        }

        return combineLatest([
            this.brandsService.fetchBrandsWithSuggestionsAndAutoRestockConnection(),
            this.brandShopService.getBrand(brandId),
            this.landingPageModel.getPublishedLandingPage(brandId).pipe(catchError(() => of(null)))
        ]).pipe(
            map(([[brandsWithAutoRestockConnection, brandsWithSuggestionConnection], brand, landingPage]) => {
                const fcProducts = brand.fcProducts;

                // Adds landing page to fcProducts in case the brand has a published landing page
                // This is needed to set the default product suite for the brand as landing page
                // If a user clicks on a brand tile, he should be redirected to the landing page
                fcProducts.landing = {
                    blocked: false,
                    participates: !!landingPage,
                    trialPeriod: { active: false, allowedRetailers: [] }
                };

                const navigationLinks = getNavigationLinksByFcProducts(
                    brand._id,
                    fcProducts,
                    this.userService.getOrganizationId()
                );

                const brandConnectedFcProducts = getBrandFcProductsConnectionStatus(
                    brand,
                    brandsWithAutoRestockConnection,
                    brandsWithSuggestionConnection,
                    landingPage
                )
                    .filter(({ status }) => status === AccessState.Granted)
                    .map(({ product }) => product);

                if (
                    !userSelectedProductSuite ||
                    !navigationLinks.some((link) => link.route.target.includes(userSelectedProductSuite))
                ) {
                    return this.navigateToDefaultProductSuite(brandConnectedFcProducts, navigationLinks, brand);
                }

                return true;
            })
        );
    }

    private navigateToDefaultProductSuite(
        brandConnectedFcProducts: FcProductName[],
        navigationLinks: NavigationLink[],
        brand: Brand
    ): UrlTree {
        if (!brandConnectedFcProducts.length) {
            return this.navigateToDefaultProductSuiteNotConnectedToBrand(navigationLinks);
        }

        const response = this.navigateToDefaultProductSuiteConnectedToBrand(
            brand,
            brandConnectedFcProducts,
            navigationLinks
        );

        return response;
    }

    private navigateToDefaultProductSuiteNotConnectedToBrand(navigationLinks: NavigationLink[]): UrlTree {
        const fallbackUrl = _.minBy(navigationLinks, 'index')?.route?.target;
        return this.router.parseUrl(parseRouteTargetToString(fallbackUrl));
    }

    private navigateToDefaultProductSuiteConnectedToBrand(
        brand: Brand,
        brandConnectedFcProducts: FcProductName[],
        navigationLinks: NavigationLink[]
    ): UrlTree {
        const brandConnectedFcProductsRelatedBrandShopLinks: NavigationLink[] = brandConnectedFcProducts.map(
            (fcProductSuite) => brandShopNavigationDefaultLinksByFcProduct(brand._id)[fcProductSuite]
        );

        const fallbackUrl = navigationLinks.find((link) =>
            brandConnectedFcProductsRelatedBrandShopLinks.some(({ index }) => index === link.index)
        )?.route?.target;

        // Brand can be connected to FC products on trial mode that the user doesn't have access to and therefore the fallbackUrl can be undefined
        if (!fallbackUrl) {
            return this.navigateToDefaultProductSuiteNotConnectedToBrand(navigationLinks);
        }

        return this.router.parseUrl(parseRouteTargetToString(fallbackUrl));
    }
}
